import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Input, Button, Chip, Textarea } from '@heroui/react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AvatarUpload from './AvatarUpload';
import { getFirebaseErrorMessage, shouldShowError } from '../utils/firebaseErrors';

const ProfilePage = () => {
  const { currentUser, userProfile, updateUserProfile, sendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [usernameError, setUsernameError] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    displayName: '',
    photoURL: '',
    photoKey: '',
    bio: '',
  });
  const [originalUsername, setOriginalUsername] = useState('');

  useEffect(() => {
    if (userProfile) {
      const username = userProfile.username || '';
      setFormData({
        username,
        displayName: userProfile.display_name || '',
        photoURL: userProfile.photo_url || '',
        photoKey: userProfile.photo_key || '',
        bio: userProfile.bio || '',
      });
      setOriginalUsername(username);
    }
  }, [userProfile]);

  // Debounced username availability check
  const checkUsernameAvailability = useCallback(async (username) => {
    // Don't check if username hasn't changed or is empty
    if (!username || username === originalUsername) {
      setUsernameError('');
      return;
    }

    // Basic validation
    if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return;
    }
    if (username.length > 20) {
      setUsernameError('Username must be 20 characters or less');
      return;
    }
    if (!/^[a-z0-9._]+$/.test(username)) {
      setUsernameError('Username can only contain lowercase letters, numbers, dots, and underscores');
      return;
    }

    setCheckingUsername(true);
    setUsernameError('');

    try {
      const idToken = await currentUser.getIdToken();
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8080';
      const response = await fetch(`${API_BASE_URL}/api/auth/check-username?username=${encodeURIComponent(username)}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.available === false) {
        setUsernameError('Username is already taken');
      } else {
        setUsernameError('');
      }
    } catch (error) {
      // Don't show error to user for API failures
    } finally {
      setCheckingUsername(false);
    }
  }, [currentUser, originalUsername]);

  // Handle username change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.username !== originalUsername) {
        checkUsernameAvailability(formData.username);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData.username, checkUsernameAvailability, originalUsername]);

  // Helper function to detect emojis
  const containsEmoji = (text) => {
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1F018}-\u{1F270}\u{238C}-\u{2454}\u{20D0}-\u{20FF}\u{FE00}-\u{FE0F}]/u;
    return emojiRegex.test(text);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate display name
    if (name === 'displayName') {
      if (value.length < 1) {
        setDisplayNameError('Display name is required');
      } else if (value.length > 30) {
        setDisplayNameError('Display name must be 30 characters or less');
        return;
      } else if (!/^[a-zA-Z0-9 ]*$/.test(value)) {
        setDisplayNameError('Display name can only contain letters, numbers, and spaces');
        return;
      } else {
        setDisplayNameError('');
      }
    }

    // Validate and enforce lowercase for username
    if (name === 'username') {
      const lowercaseValue = value.toLowerCase();

      if (containsEmoji(lowercaseValue)) {
        setUsernameError('Username cannot contain emojis');
        setFormData((prev) => ({
          ...prev,
          [name]: lowercaseValue,
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: lowercaseValue,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarUploadComplete = async (photoKey) => {
    const oldPhotoKey = userProfile?.photo_key;

    // Update formData first
    setFormData((prev) => ({
      ...prev,
      photoKey,
    }));

    // Auto-save profile with new photoKey and clear old photoURL
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await updateUserProfile({
        photoKey,
        photoURL: '', // Clear Google/legacy photo URL when custom avatar is uploaded
      });

      // Delete old avatar from R2 (if it existed)
      if (oldPhotoKey && oldPhotoKey !== photoKey) {
        try {
          const token = await currentUser.getIdToken();
          const API_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8080';

          await fetch(`${API_URL}/api/auth/delete-avatar?key=${encodeURIComponent(oldPhotoKey)}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          // Don't show error if deletion fails - it's non-critical cleanup
        } catch (deleteError) {
        }
      }

      setMessage({ type: 'success', text: 'Profile picture updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update profile picture' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Block submission if username or display name has error
    if (usernameError) {
      setMessage({ type: 'error', text: 'Please fix the username error before saving' });
      return;
    }
    if (displayNameError) {
      setMessage({ type: 'error', text: 'Please fix the display name error before saving' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await updateUserProfile({
        username: formData.username,
        displayName: formData.displayName,
        photoURL: formData.photoURL,
        photoKey: formData.photoKey,
        bio: formData.bio,
      });
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setOriginalUsername(formData.username); // Update original username after successful save

      // Navigate back after 1.5 seconds on success
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendVerification = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await sendVerificationEmail();
      setMessage({ type: 'success', text: 'Verification email sent! Please check your inbox.' });
    } catch (error) {
      const errorMessage = getFirebaseErrorMessage(error);
      if (errorMessage && shouldShowError(error)) {
        setMessage({ type: 'error', text: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
        <Card className="max-w-md">
          <CardBody className="text-center py-12">
            <p className="text-lg text-gray-600 dark:text-slate-400">Please sign in to view your profile.</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="relative text-center mb-8">
          <Button
            isIconOnly
            variant="light"
            onPress={() => navigate(-1)}
            className="absolute left-0 top-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-slate-50">Profile Settings</h1>
          <p className="text-gray-500 dark:text-slate-400 mt-2">Manage your account information</p>
        </div>

        {/* Email Section */}
        <Card>
          <CardBody className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Email</p>
                <p className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-slate-100 truncate">{currentUser.email}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
                {currentUser.emailVerified ? (
                  <Chip color="success" variant="flat" size="sm" className="sm:size-lg">
                    ✓ Verified
                  </Chip>
                ) : (
                  <>
                    <Chip color="warning" variant="flat" size="sm" className="sm:size-lg">
                      Not Verified
                    </Chip>
                    <Button
                      size="sm"
                      color="primary"
                      variant="flat"
                      onPress={handleSendVerification}
                      isDisabled={loading}
                      className="text-xs sm:text-sm whitespace-nowrap"
                    >
                      Send Verification
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Profile Picture Section */}
        <Card>
          <CardBody className="p-6">
            <p className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-4">Profile Picture</p>
            <AvatarUpload
              currentPhotoKey={formData.photoKey}
              currentPhotoURL={formData.photoURL}
              onUploadComplete={handleAvatarUploadComplete}
            />
          </CardBody>
        </Card>

        {/* Profile Information */}
        <Card>
          <CardBody className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                name="username"
                value={formData.username}
                onChange={handleChange}
                label="Username"
                placeholder="john.doe_123"
                description={usernameError || "3-20 characters, lowercase letters, numbers, dots, and underscores only"}
                variant="bordered"
                labelPlacement="outside"
                isRequired
                isInvalid={!!usernameError}
                color={usernameError ? "danger" : checkingUsername ? "default" : "default"}
                endContent={
                  checkingUsername && formData.username !== originalUsername ? (
                    <Chip size="sm" variant="flat" color="default">
                      Checking...
                    </Chip>
                  ) : usernameError ? (
                    <Chip size="sm" variant="flat" color="danger" startContent={<span>✗</span>}>
                      Taken
                    </Chip>
                  ) : formData.username !== originalUsername && formData.username.length >= 3 ? (
                    <Chip size="sm" variant="flat" color="success" startContent={<span>✓</span>}>
                      Available
                    </Chip>
                  ) : null
                }
                classNames={{
                  label: "text-gray-700 dark:text-slate-300 font-medium",
                  description: usernameError ? "text-danger" : "text-gray-500 dark:text-slate-400 text-xs",
                }}
              />

              <Input
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                label="Display Name"
                placeholder="John Doe"
                description={displayNameError || `1-30 characters, letters, numbers, and spaces only (${30 - formData.displayName.length} remaining)`}
                variant="bordered"
                labelPlacement="outside"
                isRequired
                isInvalid={!!displayNameError}
                color={displayNameError ? "danger" : "default"}
                classNames={{
                  label: "text-gray-700 dark:text-slate-300 font-medium",
                  description: displayNameError ? "text-danger" : "text-gray-500 dark:text-slate-400 text-xs",
                }}
              />

              <Textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                label="Bio"
                placeholder="Tell us about yourself..."
                description="Optional (max 500 characters)"
                variant="bordered"
                labelPlacement="outside"
                minRows={4}
                classNames={{
                  label: "text-gray-700 dark:text-slate-300 font-medium",
                  description: "text-gray-500 dark:text-slate-400 text-xs",
                }}
              />

              {message.text && (
                <div className="flex justify-center">
                  <Chip
                    color={message.type === 'success' ? 'success' : 'danger'}
                    variant="flat"
                    size="lg"
                    className="py-3 px-6"
                  >
                    {message.text}
                  </Chip>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="flat"
                  size="lg"
                  onPress={() => navigate(-1)}
                  className="flex-1 font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  size="lg"
                  isLoading={loading}
                  className="flex-1 font-semibold"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>

        {/* Privacy Notice */}
        <Card className="border-2 border-primary-200 dark:border-slate-700 bg-primary-50/50 dark:bg-slate-800/50">
          <CardBody className="p-6">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 rounded-full bg-primary-500 dark:bg-slate-600 flex items-center justify-center">
                  <span className="text-white text-sm font-bold">i</span>
                </div>
              </div>
              <div>
                <p className="font-semibold text-primary-900 dark:text-slate-200 mb-1">Privacy Information</p>
                <p className="text-sm text-primary-800 dark:text-slate-300">
                  Your <strong>username</strong> and <strong>display name</strong> are shown to other users in comments and posts. Your real name is never displayed publicly.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
